export type DocFileType = "pdf" | "excel" | "image" | "html" | "text" | "other";

export function getFileType(filename: string): DocFileType {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf") return "pdf";
  // CSV is rendered by the Excel viewer too (XLSX.read auto-detects it), so
  // tabular sources share the same grid + column show/hide affordances.
  if (["xlsx", "xls", "csv"].includes(ext)) return "excel";
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext))
    return "image";
  if (["html", "htm"].includes(ext)) return "html";
  if (ext === "txt") return "text";
  return "other";
}

export function resolveMimeType(blob: Blob, fileType: DocFileType): string {
  if (blob.type && blob.type !== "application/octet-stream") return blob.type;
  if (fileType === "pdf") return "application/pdf";
  if (fileType === "excel")
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (fileType === "html") return "text/html";
  return blob.type;
}

export interface ProcessedDoc {
  documentPdfData: Uint8Array | null;
  documentArrayBuffer: ArrayBuffer | null;
  documentBlobUrl: string | null;
  htmlContent: string | null;
}

/**
 * Convert a fetched blob into the shapes AttachmentPreviewPanel consumes.
 * Caller owns revoking the returned blob URL.
 */
export async function processDocumentBlob(
  blob: Blob,
  filename: string,
): Promise<ProcessedDoc> {
  const fileType = getFileType(filename);
  const mimeType = resolveMimeType(blob, fileType);
  if (fileType === "html") {
    return {
      documentPdfData: null,
      documentArrayBuffer: null,
      documentBlobUrl: null,
      htmlContent: await blob.text(),
    };
  }
  const arrayBuffer = await blob.arrayBuffer();
  if (fileType === "pdf") {
    return {
      documentPdfData: new Uint8Array(arrayBuffer),
      documentArrayBuffer: null,
      documentBlobUrl: null,
      htmlContent: null,
    };
  }
  const typedBlob = new Blob([blob], { type: mimeType });
  const url = URL.createObjectURL(typedBlob);
  return {
    documentPdfData: null,
    documentArrayBuffer: fileType === "excel" ? arrayBuffer : null,
    documentBlobUrl: url,
    htmlContent: null,
  };
}
